import { useDashboardModel } from '../../../modelos/Dashboard/modeloDashboard';
import React, { useEffect, useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Calendar, FileWarning, User, DollarSign, BookOpen, FileText, Eye ,
  VideoOff, AlertCircle, Timer, MessageCircle, ChevronRight, MessageSquare, ArrowRight
} from 'lucide-react'; // Íconos para un mejor UI
import { useConfiguracion } from '../../../metodos/ConfiguracionContext';
import { formatearFechaCompleta, formatearFechaCorta, formatearPrecio } from '../../../utils/utils';
import { useNavigate } from 'react-router-dom';
import { convertirFechaFirebase } from '../../../objetos/utiles_objetos';
import { obtenerNombreEmpresaReal } from '../../../config/unificacion';
import { determinarColorInfo } from '../../../views/compartedRoutes/Contabilidad/CalendarioWidget';

//estadisticas de solicitudes
const EstadisticasSolicitudesCard = () => (
  <div className="bg-white rounded-xl shadow-lg p-6 h-96">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Estadísticas de Solicitudes</h2>
    <p className="text-gray-500">Aquí irán las gráficas y métricas de solicitudes...</p>
  </div>
);

//PROUNAD
const EstadisticasProUnadCard = ({estadisticasProUnad,LoadingestadisticasProUnad,errorestadisticasProUnad}) => {
  const [clienteExpandido, setClienteExpandido] = useState(null);
  const navigate = useNavigate();

  const todasLasProximasEntregas = useMemo(() => {
    if (!estadisticasProUnad) return [];

    // Usamos flatMap para unir todas las entregas de todos los clientes en un solo array
    return estadisticasProUnad
      .flatMap(cliente =>
        // A cada entrega le añadimos el número de cliente al que pertenece
        cliente.proximasEntregas.map(entrega => ({
          ...entrega,
          numeroCliente: cliente.numeroCliente,
          nombreCliente: cliente.nombreCliente,
        }))
      )
      // Ordenamos por fecha más cercana primero (ascendente)
      .sort((a, b) => new Date(a.fechaEntrega) - new Date(b.fechaEntrega));
  }, [estadisticasProUnad]);

  const handleToggleCliente = (numeroCliente) => {
    // Si se hace clic en el cliente ya abierto, se cierra. Si no, se abre el nuevo.
    setClienteExpandido(prev => (prev === numeroCliente ? null : numeroCliente));
  };

  const handleVerServicio = (servicio) => {
    // Navegar a detalles de solicitud
    navigate(`/contabilidad/detalles_servicio/${servicio.nombreEmpresa}/${servicio.codigo}`);
  };
  

  if (LoadingestadisticasProUnad) return <div className="p-8">Cargando dashboard... ⏳</div>;
  if (errorestadisticasProUnad) return <div className="p-8 text-red-500">Error al cargar el dashboard. ❌</div>;

  return (
    <div className="font-sans">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Estadisticas plataforma Unad</h1>

      {/* Contenedor principal que apila las filas de widgets */}
      <div className="flex flex-col gap-8">
        
        {/* --- PRIMERA FILA DE WIDGETS --- */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Card de Próximas Entregas (Más pequeño) */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Calendar size={24} className="mr-3 text-indigo-600" /> {/* Icono principal para el título */}
            Próximas Entregas
          </h2>
          <div className="space-y-4 max-h-96 overflow-y-auto pr-2 custom-scrollbar"> {/* Clase para scrollbar */}
            {todasLasProximasEntregas.length > 0 ? (
              todasLasProximasEntregas.map((entrega) => (
                <div key={`${entrega.numeroCliente}-${entrega.codigo}`} className="p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-100 transition duration-200 ease-in-out hover:shadow-md hover:border-indigo-200">
                  <p className="font-bold text-indigo-700 flex items-center text-lg mb-1">
                    <BookOpen size={20} className="mr-2 text-indigo-500" /> {/* Icono para el Código */}
                    {entrega.codigo}
                  </p>
                  <p className="text-sm text-gray-700 flex items-center mt-2">
                    <User size={16} className="mr-2 text-gray-500" />
                    Cliente #{entrega.numeroCliente} - {entrega.nombreCliente}
                  </p>
                  <p className="text-sm text-gray-700 flex items-center mt-1">
                    <FileText size={16} className="mr-2 text-gray-500" /> {/* Icono para el Título del servicio */}
                    {entrega.titulo}
                  </p>
                  <p className="text-sm font-semibold flex items-center mt-2 text-red-600">
                    <Calendar size={16} className="mr-2" /> {/* Icono para la Fecha de Entrega */}
                    {new Date(entrega.fechaEntrega).toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })} {/* Incluimos el año */}
                  </p>

                  <div className="mt-4 pt-3 border-t border-gray-200"> {/* Separador y margen superior para el botón */}
                    <button
                      onClick={() => handleVerServicio(entrega)}
                      className="w-full flex items-center justify-center bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 ease-in-out shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75"
                    >
                      <Eye size={18} className="mr-2" /> {/* Icono de 'Ver' */}
                      Ver Servicio
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 p-4 text-center">🎉 No hay entregas pendientes. ¡Buen trabajo!</p>
            )}
          </div>
          </div>

          {/* Card de Lista de Clientes (Más pequeño) */}
          <div className="lg:col-span-2 bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Resumen de Clientes</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-2"> {/* ALTURA REDUCIDA */}
              {estadisticasProUnad.length > 0 ? (
                estadisticasProUnad.map((cliente) => (
                  <div key={cliente.numeroCliente} className="border rounded-lg">
                    <button onClick={() => handleToggleCliente(cliente.numeroCliente)} className="w-full flex justify-between items-center p-4 text-left">
                      <p className="font-bold">Cliente {cliente.nombreCliente} | <span className="text-green-600">{((cliente.percentPagado || 0) * 100).toFixed(0)}% Pagado</span></p>
                      {clienteExpandido === cliente.numeroCliente ? <ChevronUp /> : <ChevronDown />}
                    </button>
                    {clienteExpandido === cliente.numeroCliente && (
                      <div className="px-4 pb-4 border-t grid grid-cols-2 gap-x-4 gap-y-2 pt-4 text-sm">
                        <p><strong>Numero de cliente:</strong> {cliente.numero}</p>
                        <p><strong>Total a Pagar:</strong> ${formatearPrecio(cliente.pagoTotal)}</p>
                        <p><strong>Total Pagado:</strong> ${formatearPrecio(cliente.pagadoCliente)}</p>
                        <p><strong>Servicios (Semestre):</strong> {cliente.numeroDeServicios}</p>
                        <p><strong>Trabajos Pendientes:</strong> {cliente.numeroTrabajosPendientes}</p>
                        <p><strong>Verificados (Admin):</strong> {cliente.numeroTrabajosCheckeadosAdmin}</p>
                      </div>
                    )}
                  </div>
                ))
              ) : <p className="text-sm text-gray-500">No se encontraron clientes.</p>}
            </div>
          </div>
        </div>

        {/* --- SEGUNDA FILA DE WIDGETS --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
         
        </div>
        
        {/* Puedes añadir una tercera fila aquí si lo necesitas */}

      </div>
    </div>
  );

};

//Estadisticas de tesis
const EstadisticasTesis = () => (
  <div className="bg-white rounded-xl shadow-lg p-6 h-96">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Estadísticas Generales contabilidad</h2>
    <p className="text-gray-500">Aquí irán otras estadísticas generales de ProUnad...</p>
  </div>
);

//ESTADISTICAS TRABAJOS NO ENTREGADOS
const EstadisticasTrabajosNoEntregados = ({ estadisticasTrabajosNoEntregados, LoadingestadisticasTrabajosNoEntregados, errorestadisticasTrabajosNoEntregados }) => {

  // 2. Ordena los datos por el valor del balancePago de mayor a menor
  const trabajosOrdenados = useMemo(() => {
    if (!estadisticasTrabajosNoEntregados) return [];

    return [...estadisticasTrabajosNoEntregados].sort((a, b) => {
      // Usamos tu función para convertir ambos lados
      const fechaA = convertirFechaFirebase(a.fechaentrega);
      const fechaB = convertirFechaFirebase(b.fechaentrega);

      // Restamos para ordenar: Antigua -> Reciente
      return fechaA - fechaB;
    });
  }, [estadisticasTrabajosNoEntregados]);

  console.log("trabajos ordenados",trabajosOrdenados);

  if (LoadingestadisticasTrabajosNoEntregados) return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center">Cargando trabajos... ⏳</div>;
  if (errorestadisticasTrabajosNoEntregados) return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center text-red-500">Error al cargar datos. ❌</div>;
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex flex-col">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Trabajos Vencidos No Entregados</h2>
      <p className="text-sm text-gray-500 mb-4">Total: {trabajosOrdenados.length}</p>
      
      {/* Contenedor de la lista con scroll */}
      <div className="flex-grow overflow-y-auto pr-2 space-y-3">
        {trabajosOrdenados.length > 0 ? (
          trabajosOrdenados.map((trabajo) => {
            // 1. Obtenemos el objeto de colores para este trabajo específico
            const colores = determinarColorInfo(
              true, 
              trabajo.identificadorcodigo, 
              trabajo.getTrabajoEntregado(), 
              trabajo.avisoClienteEntrega, 
              trabajo.getTrabajoEntregadoCliente(), 
              trabajo
            );
            

            return (
              <div 
                key={trabajo.codigo} 
                // 2. Aplicamos el color de fondo dinámicamente. 
                // He cambiado bg-gray-50 por `${colores.bg} bg-opacity-10` para que el fondo sea suave
                // o puedes usar simplemente colores.bg si quieres el color sólido.
                className={`flex justify-between items-center p-3 rounded-lg border-l-4 ${colores.bg} bg-opacity-10 ${colores.border}`}
              >
                <p className="flex items-center text-gray-700 font-medium">
                  {/* 3. También puedes usar el color en el icono o texto */}
                  <FileWarning size={16} className={`mr-2 ${colores.text}`} />
                  {trabajo.codigo}
                </p>
                
                <p className="flex items-center text-gray-800 font-semibold">
                  <DollarSign size={16} className="mr-1 text-green-600" />
                  {formatearPrecio(trabajo.preciocobrado)}
                </p>

                <p className="flex items-center text-gray-800 font-semibold">
                  {/* Cambié el icono a Calendar si es fecha, por semántica */}
                  <Calendar size={16} className="mr-1 text-blue-600" />
                  {formatearFechaCorta(trabajo.fechaentrega)}
                </p>
                
                {/* Badge visual del estado */}
                <span className={`text-xs px-2 py-1 rounded-full text-white ${colores.bg}`}>
                  {trabajo.nombreEmpresa}
                </span>
              </div>
            );
          })
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <p>¡Excelente! No hay trabajos vencidos sin entregar.</p>
          </div>
        )}
      </div>
      
    </div>
  );
}
  
//Estadisticas de pagos de clientes pendientes
//IDENTIFICAR CLIENTES UNAD
const EstadisticasPagosPendientesClientes = ({ estadistigasPagosPendientes, LoadingestadistigasPagosPendientes, errorestadistigasPagosPendientes }) => {

  // 1. Estado para controlar qué cliente está expandido
  const [clienteExpandido, setClienteExpandido] = useState(null);

  const clientesOrdenados = useMemo(() => {
    if (!estadistigasPagosPendientes) return [];
    // La data que pasaste ya tiene 'totalDeuda', así que esto funciona perfecto.
    return [...estadistigasPagosPendientes].sort((a, b) => b.totalDeuda - a.totalDeuda);
  }, [estadistigasPagosPendientes]);

  // 2. Función para manejar el clic y expandir/colapsar
  const handleToggleCliente = (clienteId) => {
    // Si se hace clic en el cliente ya abierto, se cierra (null). Si no, se abre el nuevo.
    setClienteExpandido(prevId => (prevId === clienteId ? null : clienteId));
  };

  if (LoadingestadistigasPagosPendientes) return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center">Cargando saldos... ⏳</div>;
  if (errorestadistigasPagosPendientes) return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center text-red-500">Error al cargar los saldos. ❌</div>;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex flex-col">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Clientes con Pagos Pendientes</h2>
      <p className="text-sm text-gray-500 mb-4">Total: {estadistigasPagosPendientes.length}</p>

      <div className="flex-grow overflow-y-auto pr-2 space-y-3">
        {clientesOrdenados && clientesOrdenados.length > 0 ? (
          clientesOrdenados.map((item) => (
            // 3. Estructura de Acordeón Desplegable
            <div key={item.cliente} className="bg-gray-50 rounded-lg border">
              <button
                onClick={() => handleToggleCliente(item.cliente)}
                className="w-full flex justify-between items-center p-3 text-left"
              >
                <p className="flex items-center text-gray-700 font-medium">
                  <User size={16} className="mr-2 text-gray-500" />
                  {item.cliente}
                </p>
                <div className="flex items-center">
                  <p className="flex items-center text-red-600 font-bold mr-4">
                    <DollarSign size={16} className="mr-1" />
                    {formatearPrecio(item.totalDeuda)}
                  </p>
                  {clienteExpandido === item.cliente ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
              </button>

              {/* Contenido que se muestra u oculta */}
              {clienteExpandido === item.cliente && (
                <div className="px-4 pb-3 border-t border-gray-200">
                  {item.cliente}
                  <h4 className="text-sm font-bold text-gray-600 my-2">Servicios con Deuda:</h4>
                  <div className="space-y-2">
                    {/* 4. Mapeo de la lista de servicios con deuda */}
                    {item.serviciosConDeuda.map(servicio => (
                      <div key={servicio.codigo} className="flex justify-between text-sm">
                        <span className="text-gray-600">{servicio.codigo}</span>
                        <span className="font-medium text-red-500">{formatearPrecio(servicio.deuda)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <p>🎉 No hay clientes con saldos pendientes.</p>
          </div>
        )}
      </div>
    </div>
  );
};

//estadisticas de asesoriras sin enlace
const EstadisticasAsesoriasSinGrabacion = ({ 
  estadisticasAsesorias, 
  LoadingestadisticasAsesorias, 
  errorestadisticasAsesorias 
}) => {

  // 1. Ordenar por fecha: De la más antigua a la más reciente
  const asesoriasOrdenadas = useMemo(() => {
    if (!estadisticasAsesorias) return [];

    return [...estadisticasAsesorias].sort((a, b) => {
      // Usamos la función auxiliar que definimos antes
      const fechaA = convertirFechaFirebase(a.fechaentrega);
      const fechaB = convertirFechaFirebase(b.fechaentrega);

      // Orden Ascendente (Antigua -> Reciente)
      return fechaA - fechaB;
    });
  }, [estadisticasAsesorias]);

  // 2. Estados de Carga y Error
  if (LoadingestadisticasAsesorias) return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center">
      Cargando asesorías... ⏳
    </div>
  );

  if (errorestadisticasAsesorias) return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center text-red-500">
      Error al cargar datos. ❌
    </div>
  );

  // 3. Renderizado Principal
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex flex-col">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Asesorías Sin Grabación</h2>
      <p className="text-sm text-gray-500 mb-4">Total pendientes: {asesoriasOrdenadas.length}</p>
      
      {/* Contenedor de la lista con scroll */}
      <div className="flex-grow overflow-y-auto pr-2 space-y-3">
        {asesoriasOrdenadas.length > 0 ? (
          asesoriasOrdenadas.map((asesoria) => (
            <div key={asesoria.codigo} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
              
              {/* Columna 1: Código e Icono de Video Faltante */}
              <p className="flex items-center text-gray-700 font-medium w-1/3">
                <VideoOff size={16} className="mr-2 text-red-500" />
                <span className="truncate" title={asesoria.codigo}>
                  {asesoria.codigo}
                </span>
              </p>

              {/* Columna 2: Fecha (Prioridad alta según tu petición) */}
              <p className="flex items-center text-gray-800 font-semibold justify-end w-1/3">
                <Calendar size={16} className="mr-1 text-blue-600" />
                {formatearFechaCorta(asesoria.fechaentrega)}
              </p>
               
               {/* Columna 3: Precio (Opcional, si no lo necesitas bórralo, pero mantiene la simetría) */}
               {/* Si tu objeto no tiene 'balancePago', puedes quitar este bloque */}
               {asesoria.balancePago !== undefined && (
                  <p className="flex items-center text-gray-800 font-semibold justify-end w-1/3">
                    <DollarSign size={16} className="mr-1 text-green-600" />
                    {formatearPrecio(asesoria.balancePago)}
                  </p>
               )}

            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-500">
            <VideoOff size={48} className="text-gray-300 mb-2" />
            <p>¡Excelente! Todas las asesorías tienen grabación.</p>
          </div>
        )}
      </div>
    </div>
  );
};

//solicitudes por agendar
// ESTADISTICAS SOLICITUDES POR AGENDAR
const EstadisticasAgendarSolicitudes = ({ 
  solicitudPorAgendar, 
  LoadingsolicitudPorAgendar, 
  errorsolicitudPorAgendar,
  nombreEmpresaReal 
}) => {
  const navigate = useNavigate();

  const handleRevisar = (solicitud) => {
    navigate(`/dashboard/detalle_solicitud/${nombreEmpresaReal}/${solicitud.idcotizacion}?tab=pagos`);
  };

  if (LoadingsolicitudPorAgendar) return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center">
      Cargando solicitudes... ⏳
    </div>
  );

  if (errorsolicitudPorAgendar) return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center text-red-500">
      Error al cargar solicitudes por agendar. ❌
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <BookOpen size={24} className="mr-2 text-blue-600" />
          Solicitudes por Agendar
        </h2>
        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2.5 py-0.5 rounded-full">
          {solicitudPorAgendar?.length || 0}
        </span>
      </div>

      <div className="flex-grow overflow-y-auto pr-2 space-y-3 custom-scrollbar">
        {solicitudPorAgendar && solicitudPorAgendar.length > 0 ? (
          solicitudPorAgendar.map((solicitud) => (
            <div key={solicitud.id} className="bg-gray-50 border border-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">ID Cotización</p>
                  <p className="font-mono font-bold text-blue-700">{solicitud.idcotizacion}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Monto</p>
                  <p className="font-bold text-green-600">{formatearPrecio(solicitud.precioCobradoCliente)}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">Materia / Tema</p>
                <p className="text-sm font-semibold text-gray-800 truncate" title={solicitud.materia}>
                  {solicitud.materia}
                </p>
              </div>

              <button
                onClick={() => handleRevisar(solicitud)}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold py-2 px-4 rounded-lg transition-colors shadow-sm"
              >
                <Calendar size={16} />
                Revisar y Agendar
              </button>
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-500 text-center">
            <div className="bg-gray-100 p-4 rounded-full mb-3">
              <FileText size={32} className="text-gray-300" />
            </div>
            <p className="text-sm">No hay solicitudes pendientes de agendamiento.</p>
          </div>
        )}
      </div>
    </div>
  );
};

//Revisiones
const EstadisticasRevision = ({
  serviciosPorRevisar, 
  LoadingserviciosPorRevisar, 
  errorserviciosPorRevisar,
  nombreEmpresaReal 
}) => {
  const navigate = useNavigate();

  // Función para calcular días transcurridos
  const calcularDiasEnRevision = (fechaInicio) => {
    if (!fechaInicio) return 0;
    const inicio = new Date(fechaInicio);
    const hoy = new Date();
    const diferencia = hoy.getTime() - inicio.getTime();
    return Math.max(0, Math.floor(diferencia / (1000 * 60 * 60 * 24)));
  };

  // Función para obtener el texto del último mensaje si es formato Quill (JSON/Delta)
  const obtenerSnippetMensaje = (contenido) => {
    if (!contenido) return "Sin mensaje";
    if (Array.isArray(contenido)) {
      // Extrae el texto del primer insert si es un Delta de Quill
      return contenido[0]?.insert?.substring(0, 30) + "..." || "Mensaje enriquecido";
    }
    return contenido.toString().substring(0, 30) + "...";
  };

  if (LoadingserviciosPorRevisar) return (
    <div className="bg-white rounded-3xl shadow-sm p-10 h-[500px] flex flex-col items-center justify-center border border-gray-100">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-slate-800 mb-4"></div>
      <p className="text-slate-400 font-black uppercase tracking-widest text-xs">Sincronizando Revisiones...</p>
    </div>
  );

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 flex flex-col overflow-hidden">
      {/* HEADER */}
      <div className="p-6 bg-slate-50 border-b border-gray-100 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tighter uppercase flex items-center gap-3">
            <div className="p-2 bg-orange-500 rounded-lg shadow-lg">
                <Timer size={20} className="text-white" />
            </div>
            Monitoreo de Revisiones
          </h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-1">Control de Calidad Técnica</p>
        </div>
        <span className="bg-orange-100 text-orange-700 text-[10px] font-black px-3 py-1 rounded-full border border-orange-200">
            {serviciosPorRevisar?.length || 0} CASOS ACTIVOS
        </span>
      </div>

      {/* CUERPO - TABLA */}
      <div className="flex-1 overflow-x-auto overflow-y-auto max-h-[500px] custom-scrollbar">
        <table className="w-full text-left border-collapse">
          <thead className="sticky top-0 bg-white z-10 shadow-sm">
            <tr className="border-b border-gray-100">
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Servicio</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Inicio</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Motivo</th>
              {/* NUEVA COLUMNA DE INTERACCIÓN */}
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Última Interacción</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Días</th>
              <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {serviciosPorRevisar?.map((servicio) => {
                const dias = calcularDiasEnRevision(servicio.revision?.inicioRevision);
                
                // Lógica para detectar el último mensaje (buscamos en 'preguntas' o 'mensajes' según tu objeto)
                const listaMensajes = servicio.chatRevision || [];
                const ultimoMsg = listaMensajes.length > 0 ? listaMensajes[listaMensajes.length - 1] : null;
                
                // Determinamos si debemos responder nosotros (Admin)
                // Asumimos que si el autor NO es Admin o es "YO", es un mensaje externo que requiere atención
                const requiereRespuesta = ultimoMsg && (ultimoMsg.isAdmin === false || ultimoMsg.nombreTutor === "YO");

                return (
                  <tr key={servicio.codigo} className="hover:bg-slate-50/50 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-[9px] font-black text-slate-300 uppercase leading-none mb-1">ID: {servicio.idcontable}</span>
                        <span className="font-mono font-bold text-slate-700 text-sm">{servicio.codigo}</span>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-xs font-bold text-slate-500">
                        {servicio.revision?.inicioRevision ? formatearFechaCompleta(servicio.revision.inicioRevision) : '---'}
                    </td>

                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-[9px] font-black uppercase ${
                        servicio.revision?.motivo === 'PERDIDO' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {servicio.revision?.motivo || 'General'}
                      </span>
                    </td>

                    {/* COLUMNA INTERACCIONES DINÁMICA */}
                    <td className="px-6 py-4">
                      {ultimoMsg ? (
                        <div className="flex flex-col gap-1 max-w-[200px]">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-black text-slate-400 uppercase truncate">
                              {ultimoMsg.nombreTutor || ultimoMsg.asesor || "Usuario"}
                            </span>
                            {requiereRespuesta && (
                              <span className="animate-pulse flex h-2 w-2 rounded-full bg-blue-500" title="Respuesta pendiente"></span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-600 italic truncate leading-none">
                            "{obtenerSnippetMensaje(ultimoMsg.pregunta || ultimoMsg.mensaje)}"
                          </p>
                          {requiereRespuesta && (
                            <div className="mt-1">
                                <span className="text-[8px] font-black bg-blue-600 text-white px-1.5 py-0.5 rounded uppercase tracking-tighter">
                                    Responder ahora
                                </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-300 italic">Sin mensajes aún</span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <span className={`text-xs font-black ${dias >= 2 ? 'text-red-500' : 'text-slate-600'}`}>
                        {dias}d
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => navigate(`/contabilidad/detalles_servicio/${nombreEmpresaReal}/${servicio.codigo}`)}
                        className={`p-2 rounded-xl transition-all border ${
                            requiereRespuesta 
                            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 shadow-blue-200' 
                            : 'bg-slate-50 text-slate-400 border-slate-100 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <ArrowRight size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
  
};

//estadisticas de referidos
// ESTADISTICAS DE REFERIDOS (Comisiones por cobrar)
const EstadisticasReferidosCard = ({ estadisticasReferidos, loading, error }) => {
  const [empresaExpandida, setEmpresaExpandida] = useState(null);

  if (loading) return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center">Cargando comisiones... ⏳</div>;
  if (error) return <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex items-center justify-center text-red-500">Error al cargar referidos. ❌</div>;

  // Convertimos el objeto de empresas en un array para iterar
  const empresasOrigen = estadisticasReferidos ? Object.entries(estadisticasReferidos) : [];

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <DollarSign size={24} className="mr-2 text-green-600" />
          Comisiones por Referidos
        </h2>
      </div>

      <div className="flex-grow overflow-y-auto pr-2 space-y-4 custom-scrollbar">
        {empresasOrigen.length > 0 ? (
          empresasOrigen.map(([nombreEmpresa, datos]) => (
            <div key={nombreEmpresa} className="border rounded-xl overflow-hidden bg-gray-50">
              {/* Cabecera de la Empresa Origen */}
              <button 
                onClick={() => setEmpresaExpandida(empresaExpandida === nombreEmpresa ? null : nombreEmpresa)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-100 transition-colors"
              >
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Empresa Origen</p>
                  <p className="font-bold text-gray-800">{nombreEmpresa}</p>
                </div>
                <div className="text-right flex items-center gap-4">
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Disponible</p>
                    <p className="font-bold text-green-600">{formatearPrecio(datos.pagoReferidoTotal)}</p>
                  </div>
                  {empresaExpandida === nombreEmpresa ? <ChevronUp size={20}/> : <ChevronDown size={20}/>}
                </div>
              </button>

              {/* Detalle Expandido */}
              {empresaExpandida === nombreEmpresa && (
                <div className="p-4 bg-white border-t border-gray-100 space-y-3">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <p className="text-[9px] font-bold text-blue-600 uppercase">Total Ganado</p>
                      <p className="font-bold text-blue-800 text-sm">{formatearPrecio(datos.gananciaReferido)}</p>
                    </div>
                    <div className="bg-orange-50 p-2 rounded-lg">
                      <p className="text-[9px] font-bold text-orange-600 uppercase">A Futuro</p>
                      <p className="font-bold text-orange-800 text-sm">{formatearPrecio(datos.PagoFuturo)}</p>
                    </div>
                  </div>

                  <p className="text-xs font-black text-gray-400 uppercase mb-2">Servicios Pendientes</p>
                  <div className="space-y-2">
                    {datos.serviciosPendientesDePago.map((serv) => (
                      <div key={serv.idServicio} className="flex justify-between items-center text-xs p-2 bg-gray-50 rounded border border-gray-100">
                        <span className="font-mono font-bold text-gray-600">{serv.idServicio}</span>
                        <div className="text-right">
                            <p className="font-bold text-gray-700">{formatearPrecio(serv.montoPendiente)}</p>
                            <p className={`text-[8px] font-black uppercase ${serv.esDisponible ? 'text-green-500' : 'text-orange-400'}`}>
                                {serv.esDisponible ? 'Cobrable' : 'En Espera'}
                            </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 italic">
            <p>No hay comisiones registradas.</p>
          </div>
        )}
      </div>
    </div>
  );
};

//Modulo que integra todo
const ModuloDashboard = () => {

  const {
      //dashboard proUNAD
        estadisticasProUnad,
        LoadingestadisticasProUnad,
        errorestadisticasProUnad,
        cargarEstadisticasProUnad,

        //trabajos no entregados
        estadisticasTrabajosNoEntregados,
        LoadingestadisticasTrabajosNoEntregados,
        errorestadisticasTrabajosNoEntregados,
        cargarEstadisticasServiciosNoEntregados,

        //pagos
        estadistigasPagosPendientes,
        LoadingestadistigasPagosPendientes,
        errorestadistigasPagosPendientes,
        cargarEstadisticasPagosPendientes,

        //asesorias sin enlace
        estadistigasAsesoriasPendientes,
        LoadingestadistigasAsesoriasPendientes,
        errorestadistigasAsesoriasPendientes,
        cargarEstadisticasAsesorias,

        //solicitudes por agendar
        cargarsolicitudesPorAgendar,
        solicitudPorAgendar,
        LoadingsolicitudPorAgendar,
        errorsolicitudPorAgendar,

        //servicios por revisar
        cargarServiciosRevision,
        serviciosPorRevisar,
        LoadingserviciosPorRevisar,
        errorserviciosPorRevisar,

        //estadisticas de referidos
        estadisticasReferidos,
        loadingestadisticasReferidos,
        errorestadisticasReferidos,
        cargarEstadisticasReferidos,

  } = useDashboardModel();

  const {configuracion} = useConfiguracion();
  let nombreEmpresaReal =  obtenerNombreEmpresaReal(configuracion.nombreEmpresa);
 

  useEffect(() =>{
    //Estadisticas proUNAD, cargar semestre dinamicamente
    const fechaActual = new Date();
    const anio = fechaActual.getFullYear();
    const semestre = fechaActual.getMonth() <= 5 ? "1" : "2";
    const periodoActual = `${anio}-${semestre}`;

    cargarEstadisticasProUnad(nombreEmpresaReal,periodoActual);
    cargarEstadisticasServiciosNoEntregados(nombreEmpresaReal);
    cargarEstadisticasPagosPendientes(nombreEmpresaReal);
    cargarEstadisticasAsesorias(nombreEmpresaReal);
    cargarsolicitudesPorAgendar(nombreEmpresaReal);
    cargarServiciosRevision(nombreEmpresaReal);
    cargarEstadisticasReferidos(nombreEmpresaReal);
  },[]);

  return (
    <div className="p-4 md:p-8 bg-gray-100 min-h-screen font-sans">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard</h1>

      {/* 🧱 CONTENEDOR PRINCIPAL DEL GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

        <EstadisticasTrabajosNoEntregados
          estadisticasTrabajosNoEntregados={estadisticasTrabajosNoEntregados}
          LoadingestadisticasTrabajosNoEntregados={LoadingestadisticasTrabajosNoEntregados}
          errorestadisticasTrabajosNoEntregados={errorestadisticasTrabajosNoEntregados}
        />

        <EstadisticasPagosPendientesClientes
          estadistigasPagosPendientes={estadistigasPagosPendientes}
          LoadingestadistigasPagosPendientes={LoadingestadistigasPagosPendientes}
          errorestadistigasPagosPendientes={errorestadistigasPagosPendientes}
        />

        <EstadisticasAsesoriasSinGrabacion
          estadisticasAsesorias={estadistigasAsesoriasPendientes}
          LoadingestadisticasAsesorias={LoadingestadistigasAsesoriasPendientes}
          errorestadisticasAsesorias={errorestadistigasAsesoriasPendientes}
        />

        <EstadisticasAgendarSolicitudes 
          solicitudPorAgendar={solicitudPorAgendar}
          LoadingsolicitudPorAgendar={LoadingsolicitudPorAgendar}
          errorsolicitudPorAgendar={errorsolicitudPorAgendar}
          nombreEmpresaReal={nombreEmpresaReal}
        />

       

        <div className="md:col-span-2">
          <EstadisticasRevision 
            serviciosPorRevisar={serviciosPorRevisar}
            LoadingserviciosPorRevisar={LoadingserviciosPorRevisar}
            errorserviciosPorRevisar={errorserviciosPorRevisar}
            nombreEmpresaReal={nombreEmpresaReal}
          />
        </div>

        

        <div className="md:col-span-2">
          <EstadisticasProUnadCard
            estadisticasProUnad={estadisticasProUnad}
            LoadingestadisticasProUnad={LoadingestadisticasProUnad}
            errorestadisticasProUnad={errorestadisticasProUnad}
          />
        </div>

         <div className="md:col-span-2">
          <EstadisticasReferidosCard 
            estadisticasReferidos={estadisticasReferidos}
            loading={loadingestadisticasReferidos}
            error={errorestadisticasReferidos}
          />
        </div>

      </div>
    </div>
  );



}

export default ModuloDashboard
